/*
 * sudsy-buy-now.js
 * ─────────────────────────────────────────────────────────────────────────────
 * PURPOSE
 *   Converts every [data-sudsy-cta] button into a "Buy Now" action that:
 *   1. Clears the cart (so repeated clicks always produce a 1-unit checkout)
 *   2. Adds 1 unit of the currently selected variant
 *   3. Redirects straight to /checkout — no cart page is ever shown
 *
 * BUTTONS COVERED (all carry data-sudsy-cta)
 *   - "Buy Now" in the product form area       (blocks/buy-buttons.liquid)
 *   - Final CTA section button                 (sections/sudsy-final-cta.liquid)
 *   - Floating mobile CTA bar                  (sections/sudsy-final-cta.liquid)
 *   - How It Works CTA                         (sections/sudsy-how-it-works.liquid)
 *   - What's in the Box CTA                    (sections/sudsy-whats-in-box.liquid)
 *   - Expanded Feature CTA (when show_cta=on)  (sections/sudsy-expanded-feature.liquid)
 *
 * WHY CAPTURE PHASE + stopPropagation
 *   Horizon's product-form-component registers a bubble-phase click listener.
 *   If it fires first it makes its own /cart/add fetch against the un-cleared cart,
 *   creating a race condition that put 2+ units on the checkout page.
 *   Registering in capture phase (addEventListener 3rd arg = true) means this
 *   handler fires on the way DOWN to the target, before any bubble-phase handler.
 *   e.stopPropagation() then prevents Horizon from seeing the click at all.
 *
 * ANALYTICS (Microsoft Clarity)
 *   Every click sends a Clarity custom event, `buy_now_click`, and tags the
 *   session with `buy_now_source` (buy_box, floating_bar, sudsy_final_cta, ...).
 *
 *   Two things were making these clicks invisible to Clarity:
 *   - This listener runs BEFORE Clarity's. Both sit on document in the capture
 *     phase, and Chrome fires them in registration order: component.js, this
 *     file, sudsy-pdp.js, the OpenAI pixel, then clarity.js. Clarity reads the
 *     clicked element's text and state synchronously when its own listener
 *     runs, and by then this one had already set the button to disabled with
 *     the text "…". A Clarity rule or Smart Event on the text "Buy Now" could
 *     not match it. The visual change is therefore applied in a setTimeout, which
 *     runs after the whole click has finished dispatching to every listener.
 *     (A microtask would not do: the engine drains microtasks between
 *     listeners, so it would still run before Clarity's.)
 *   - stopPropagation() below is NOT the cause. It only stops other nodes in the
 *     path; Clarity is on the same node and phase and still fires. It does hide
 *     the click from bubble-phase listeners, which is why the explicit event
 *     below does not rely on any listener of Clarity's seeing it.
 *
 * WHY NATIVE FORM POST (not window.location.href)
 *   Horizon's page-transition.js intercepts window.location.href assignments
 *   and silently drops navigations to the checkout domain. A real form.submit()
 *   bypasses that interceptor entirely.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TO DISABLE (revert to standard Shopify cart flow)
 *   Option A — quickest: comment out or remove the <script> tag in
 *              layout/theme.liquid (search for "sudsy-buy-now.js")
 *
 *   Option B — restore standard ATC: in templates/product.sudsy.json, set
 *              buy_buttons > settings > show_add_to_cart: true
 *              Then remove data-sudsy-cta from any section buttons you want
 *              to revert, or leave this file in place and it will be a no-op
 *              for buttons without the attribute.
 *
 *   Option C — allow multiple units: remove the fetch('/cart/clear.js') call
 *              below. Buttons will still bypass the cart page but won't clear
 *              previous items first.
 *
 * NOTE: Shopify admin — Products → Sudsy → variant → Purchase options →
 *       "Max per order = 1" is a recommended server-side companion setting.
 *       Remove it there if you want to allow multi-unit purchases.
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  function getVariantId() {
    var input = document.querySelector('[data-type="add-to-cart-form"] input[name="id"]') ||
                document.querySelector('form input[name="id"]');
    return input ? input.value : null;
  }

  /* Which Buy Now it was, as a stable label for Clarity filters. The buy box
     and the floating bar are named by class; the rest by their section's id,
     whose tail after "__" is the section's name in the template. */
  function ctaSource(btn) {
    if (btn.classList.contains('sudsy-buy-now-btn')) return 'buy_box';
    if (btn.classList.contains('sudsy-floating-cta__btn')) return 'floating_bar';

    var section = btn.closest('.shopify-section');
    var id = section ? section.id : '';
    return (id.indexOf('__') > -1 ? id.split('__').pop() : id) || 'unknown';
  }

  /* Send the event and tag the session. clarity() is a queueing stub from the
     very top of the page until the real script loads, so this is safe at any
     time; it is simply absent if a blocker removed Clarity. */
  function trackBuyNow(btn) {
    if (typeof window.clarity !== 'function') return;
    try {
      window.clarity('set', 'buy_now_source', ctaSource(btn));
      window.clarity('event', 'buy_now_click');
    } catch (err) { /* analytics must never get in the way of a purchase */ }
  }

  function buyNow(btn) {
    var variantId = getVariantId();
    if (!variantId) return;

    /* Read the label now, change it later. See ANALYTICS in the header. */
    btn._savedText = btn.textContent;
    btn._buying = true;
    setTimeout(function () {
      if (!btn._buying) return; /* the request already failed and was undone */
      btn.disabled = true;
      btn.textContent = '…';
    }, 0);

    var form = document.createElement('form');
    form.method = 'post';
    form.action = '/cart/add';

    function hidden(name, value) {
      var el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      el.value = value;
      form.appendChild(el);
    }
    hidden('id', variantId);
    hidden('quantity', '1');
    hidden('return_to', '/checkout');
    document.body.appendChild(form);

    fetch('/cart/clear.js', { method: 'POST' })
      .then(function () { form.submit(); })
      .catch(function () {
        btn._buying = false;
        btn.disabled = false;
        btn.textContent = btn._savedText;
        delete btn._savedText;
        form.remove();
      });
  }

  /* Restore buttons when browser restores page from bfcache (back button). */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-sudsy-cta]').forEach(function (btn) {
      if (btn._savedText !== undefined) {
        btn.textContent = btn._savedText;
        delete btn._savedText;
      }
      btn._buying = false;
      btn.disabled = false;
    });
  });

  /* Capture phase — fires before Horizon's bubble-phase handler. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sudsy-cta]');
    if (!btn || btn.disabled || btn._buying) return;
    e.preventDefault();
    e.stopPropagation();
    trackBuyNow(btn);
    buyNow(btn);
  }, true);

})();
